import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import SVGImage from '../layout/SVGImage';
import DateSelector from '../forecast/DateSelector';
import { useTranslation } from 'react-i18next';
import { Divider } from '../common/Divider';
import { PowerOutageScheduleTimepicker } from './PowerOutageScheduleTimepicker';
import { PowerOutageScheduleInput } from './PowerOutageScheduleInput';
import { PowerOutageScheduleLimitApplyButton } from './PowerOutageScheduleLimitApplyButton';
import { PowerOutageScheduleLimitFilterButton } from './PowerOutageScheduleLimitFilterButton';
import { PowerOutageScheduleLimit } from './PowerOutageScheduleLimit';
// import { capacityTotalMwh } from '../../utils/data';
import { PowerOutageScheduleLimitPagination } from './PowerOutageScheduleLimitPagination';
import { PowerOutageScheduleMemo } from './PowerOutageScheduleMemo';
import { PowerOutageScheduleResetButton } from './PowerOutageScheduleResetButton';
import LoginSubmitButton from '@/components/common/SubmitButton';
import {
  PowerOutageScheduleFilter,
  PowerOutageScheduleFilterRadioType,
} from './PowerOutageScheduleFilter';
import {
  addDays,
  setHours,
  setMilliseconds,
  setMinutes,
  setSeconds,
} from 'date-fns';
import { PowerOutageScheduleErrorMessage } from './PowerOutageScheduleErrorMessage';
import { useMediaQuery } from 'react-responsive';
import { PowerOutageScheduleLimitFilterBadge } from './PowerOutageScheduleLimitFilterBadge';
import { get, patch, post } from '../../utils/fetch';
import { formatInTimeZone } from 'date-fns-tz';
import { LimitDataNotValidateException } from '../../exceptions/validationException';

interface PowerOutageScheduleInsertModalProps {
  testId?: string;
  className?: string;
  onClick?: () => void;
  limitDataInitialValue: Array<PowerOutageScheduleLimitData>;
  capacityGenMw?: string;
  startDate?: Date;
  endDate?: Date;
  isUpdate?: boolean;
  siteId?: string;
}

enum PowerOutageScheduleDateType {
  START_DATE = 'start_date',
  END_DATE = 'end_date',
  START_TIME = 'start_time',
  END_TIME = 'end_time',
}

const INITIAL_PAGINATION_INDEX = 0;

export const setTimes = (
  date: Date,
  hour: number,
  minute: number = 0
): Date => {
  return setMilliseconds(
    setSeconds(setMinutes(setHours(date, hour), minute), 0),
    0
  );
};

export const PowerOutageScheduleInsertModal: React.FC<
  PowerOutageScheduleInsertModalProps
> = ({
  siteId,
  testId,
  onClick,
  className,
  limitDataInitialValue,
  capacityGenMw,
  startDate,
  endDate,
  isUpdate,
}) => {
  const { t } = useTranslation();
  const [showFilter, setShowFilter] = useState(false);
  const [currentIndex, setCurrentIndex] = useState<number | null>(0);
  const [dateErrorMessage, setDateErrorMessage] = useState({
    isError: false,
    message: '',
  });
  const [changedDateType, setChangedDateType] =
    useState<PowerOutageScheduleDateType | null>(null);
  const isMacbookAir = useMediaQuery({ query: '(max-height: 1000px)' });

  const [siteStopScheduleEntityParams, setSiteStopScheduleEntityParams] =
    useState<SiteStopScheduleEntityParams>({
      [PowerOutageScheduleDateType.START_DATE]: startDate
        ? startDate
        : new Date(),
      [PowerOutageScheduleDateType.END_DATE]: endDate
        ? endDate
        : addDays(new Date(), 1),
      [PowerOutageScheduleDateType.START_TIME]: startDate
        ? startDate
        : setTimes(new Date(), 12),
      [PowerOutageScheduleDateType.END_TIME]: endDate
        ? endDate
        : setTimes(addDays(new Date(), 1), 12),
      memo: '',
    });

  const handleTextChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setSiteStopScheduleEntityParams((prev) => ({
        ...prev,
        memo: event.target.value,
      }));
    },
    [setSiteStopScheduleEntityParams]
  );

  const handleClearButton = (
    initialValue: Array<PowerOutageScheduleLimitData>
  ) => {
    setLimitDataArray(initialValue);
    setCurrentIndex(0);
  };

  const getInputBaseClassName = useCallback(
    (type: PowerOutageScheduleDateType) => {
      switch (type) {
        case PowerOutageScheduleDateType.START_DATE:
        case PowerOutageScheduleDateType.END_DATE:
          return type === changedDateType && dateErrorMessage.isError
            ? 'h-[36px] border-[1px] border-light-6 bg-white border-red-main'
            : 'h-[36px] border-[1px] border-light-6 bg-white';
        case PowerOutageScheduleDateType.START_TIME:
        case PowerOutageScheduleDateType.END_TIME:
          return type === changedDateType && dateErrorMessage.isError
            ? 'ml-[10px] h-[36px] [&>input:last-child]:mr-[18px] [&>input:nth-child(2)]:ml-[18px] [&>input]:w-[15px] [&>span]:mx-[14px] border-red-main'
            : 'ml-[10px] h-[36px] [&>input:last-child]:mr-[18px] [&>input:nth-child(2)]:ml-[18px] [&>input]:w-[15px] [&>span]:mx-[14px]';
      }
    },

    [changedDateType, dateErrorMessage.isError]
  );

  const validateDates = () => {
    const startDate = mergeDates(
      siteStopScheduleEntityParams[PowerOutageScheduleDateType.START_DATE],
      siteStopScheduleEntityParams[PowerOutageScheduleDateType.START_TIME]
    );
    const endDate = mergeDates(
      siteStopScheduleEntityParams[PowerOutageScheduleDateType.END_DATE],
      siteStopScheduleEntityParams[PowerOutageScheduleDateType.END_TIME]
    );
    return startDate < endDate;
  };

  const mergeDates = (datePart: Date, timePart: Date): Date => {
    return setMilliseconds(
      setSeconds(
        setMinutes(
          setHours(datePart, timePart.getHours()),
          timePart.getMinutes()
        ),
        0
      ),
      0
    );
  };

  const decimalPoint = useRef(10000);

  const calcKw = (percent: number) =>
    capacityGenMw
      ? percent * (1 / 100) * parseFloat(capacityGenMw) * 1000
      : null;

  const [filters, setFilters] = useState<PowerOutageScheduleFilterData>({
    type: PowerOutageScheduleFilterRadioType.Generator,
    searchKeyword: '',
    isIncludeLock: false,
  });

  const hasKeywordFilter = (filters: PowerOutageScheduleFilterData) =>
    filters.searchKeyword && filters.searchKeyword.length > 0;

  const hasLockFilter = (filters: PowerOutageScheduleFilterData) =>
    filters.isIncludeLock;

  const showFilterDot = useMemo(
    () => hasKeywordFilter(filters) || hasLockFilter(filters),
    [filters, hasKeywordFilter, hasLockFilter]
  );

  const [limitDataArray, setLimitDataArray] = useState<
    Array<PowerOutageScheduleLimitData>
  >(limitDataInitialValue.map((data) => ({ ...data })));

  // filtered version of above limit data
  const [filteredLimitData, setFilteredLimitData] = useState<
    Array<PowerOutageScheduleLimitData>
  >([...limitDataArray]);

  const filterData = (
    type: PowerOutageScheduleFilterRadioType,
    keyword: string,
    isLock: boolean
  ) => {
    setFilters({
      ...filters,
      type,
      searchKeyword: keyword,
      isIncludeLock: isLock,
    });
    setCurrentIndex(INITIAL_PAGINATION_INDEX);
  };

  const checkIsValidLimitData = (
    limitData: Array<PowerOutageScheduleLimitData>
  ) =>
    limitData.every((v) =>
      v.value !== null && typeof v.value === 'string'
        ? !isNaN(parseFloat(v.value))
        : true
    );

  const [limitDataError, setLimitDataError] = useState<{
    isError: boolean;
    message: string | null;
    index: number | null;
  }>({
    isError: false,
    message: null,
    index: null,
  });

  const calcPowerPercentFromLimitData = useCallback(() => {
    if (!checkIsValidLimitData(limitDataArray)) {
      return null;
    }
    return (
      Math.round(
        (limitDataArray.reduce(
          (prev, cur) =>
            prev +
            (typeof cur.value === 'string'
              ? parseFloat(cur.value as string)
              : (cur.value as number)),
          0
        ) /
          limitDataArray.length) *
          decimalPoint.current
      ) / decimalPoint.current
    );
  }, [limitDataArray, decimalPoint, setLimitDataError]);

  const initialPowerPercent = useRef(calcPowerPercentFromLimitData());

  const [availabilityData, setAvailabilityData] = useState<{
    powerPercent: number | string | null;
    kw: number | string | null;
    changed: string | null;
  }>({
    powerPercent: initialPowerPercent.current,
    kw: calcKw(
      initialPowerPercent.current &&
        typeof initialPowerPercent.current === 'number'
        ? initialPowerPercent.current
        : 100
    ),
    changed: null,
  });

  const kwMaxRange = calcKw(100);

  const calcPercent = useCallback(
    (kw: number) =>
      kwMaxRange
        ? Math.round((kw / kwMaxRange) * 100 * decimalPoint.current) /
          decimalPoint.current
        : null,
    [kwMaxRange]
  );

  const [availabilityDataError, setAvailabilityDataError] = useState<{
    isError: boolean;
    message: string | null;
  }>({
    isError: false,
    message: null,
  });

  const isDisabled = filters.isIncludeLock || filters.searchKeyword.length > 0;

  const applyAvailabilityData = () => {
    const availability = availabilityData.powerPercent;
    if (isDisabled) {
      setLimitDataError({
        isError: true,
        message: t('overview.modal.error.disabledApplyButton'),
        index: null,
      });
    } else if (availability === null || typeof availability === 'string') {
      setAvailabilityDataError({
        isError: true,
        message: t('overview.modal.error.invalidFormat'),
      });
    } else {
      const unlockData = limitDataArray.filter((v) => !v.isLock);
      const lockSum = limitDataArray.reduce((prev, cur, i) => {
        // -1 indicates error
        if (prev === -1) return prev;
        if (cur.value === null) {
          setLimitDataError({
            isError: true,
            message: t('overview.modal.error.invalidFormat'),
            index: i,
          });
          return -1;
        }

        const parsedValue =
          typeof cur.value === 'string' ? parseFloat(cur.value) : cur.value;
        if (isNaN(parsedValue)) {
          setLimitDataError({
            isError: true,
            message: t('overview.modal.error.invalidFormat'),
            index: i,
          });
          return -1;
        }
        prev += cur.isLock ? parsedValue : 0;
        return prev;
      }, 0);
      // if it has error return
      if (lockSum === -1) return;

      // reset error if exist
      if (limitDataError.isError)
        setLimitDataError({
          isError: false,
          message: null,
          index: null,
        });

      const unlockValue =
        Math.round(
          (Math.abs(availability * limitDataArray.length - lockSum) /
            unlockData.length) *
            decimalPoint.current
        ) / decimalPoint.current;
      setLimitDataArray(
        limitDataArray.map((v) => ({
          ...v,
          value: v.isLock ? v.value : unlockValue.toString(),
        }))
      );
    }
  };

  const handleCloseFilter = useCallback(() => {
    setShowFilter(false);
  }, [setShowFilter]);

  const [isSubmit, setIsSubmit] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmit(true);

    const powerPercent = calcPowerPercentFromLimitData();
    if (powerPercent === null) {
      setIsSubmit(false);
      throw new LimitDataNotValidateException();
    }

    const requestBody = {
      site: siteId,
      startTime: formatInTimeZone(
        mergeDates(
          siteStopScheduleEntityParams.start_date,
          siteStopScheduleEntityParams.start_time
        ),
        'Asia/Seoul',
        "yyyy-MM-dd'T'HH:mm:ssXXX"
      ),
      endTime: formatInTimeZone(
        mergeDates(
          siteStopScheduleEntityParams.end_date,
          siteStopScheduleEntityParams.end_time
        ),
        'Asia/Seoul',
        "yyyy-MM-dd'T'HH:mm:ssXXX"
      ),
      memo: siteStopScheduleEntityParams.memo,
      utilRate_site: powerPercent,
      utilRate_generators: limitDataArray.map((limitData) => ({
        number: parseInt(limitData.name),
        value:
          typeof limitData.value === 'number'
            ? limitData.value
            : parseFloat(limitData.value as string),
      })),
    };

    const response = isUpdate
      ? await patch('/customer/site/stop-schedule', {
          body: JSON.stringify(requestBody),
        })
      : await post('/customer/site/stop-schedule', {
          body: JSON.stringify(requestBody),
        });

    setIsSubmit(false);

    if (response.status === 'success') {
      onClick?.();
    } else {
      setLimitDataError({
        isError: true,
        message: response.message,
        index: null,
      });
    }
  };

  useEffect(() => {
    const filteredLimitData = limitDataArray.filter(
      (v) =>
        (filters.type === PowerOutageScheduleFilterRadioType.Generator
          ? v.name.includes(filters.searchKeyword)
          : (typeof v.value === 'number'
              ? v.value.toString()
              : v.value
            )?.includes(filters.searchKeyword)) &&
        (filters.isIncludeLock ? v.isLock === true : true)
    );
    setFilteredLimitData(filteredLimitData);
  }, [filters, limitDataArray]);

  useEffect(() => {
    if (availabilityData.changed) {
      if (
        availabilityData.changed === 'kw' &&
        availabilityData.kw !== null &&
        typeof availabilityData.kw === 'number'
      ) {
        const powerPercent = calcPercent(availabilityData.kw);
        setAvailabilityData((prev) => ({
          ...prev,
          powerPercent,
          changed: null,
        }));
      } else if (
        availabilityData.changed === 'powerPercent' &&
        availabilityData.powerPercent !== null &&
        typeof availabilityData.powerPercent === 'number'
      ) {
        const kw = calcKw(availabilityData.powerPercent);
        setAvailabilityData((prev) => ({ ...prev, kw, changed: null }));
      }
    }
  }, [availabilityData]);

  useEffect(() => {
    if (checkIsValidLimitData(limitDataArray)) {
      // update availability data
      const powerPercent = calcPowerPercentFromLimitData();
      setAvailabilityData((prev) => ({
        ...prev,
        powerPercent,
        changed: 'powerPercent',
      }));
    }
  }, [limitDataArray]);

  useEffect(() => {
    const validateResult = validateDates();
    if (!validateResult) {
      setDateErrorMessage({
        isError: true,
        message: t('overview.modal.error.dateMismatch'),
      });
    } else {
      setDateErrorMessage({
        isError: false,
        message: '',
      });
    }

    if (siteId && !isUpdate) {
      const requestBody = {
        site: siteId,
        startTime: formatInTimeZone(
          mergeDates(
            siteStopScheduleEntityParams.start_date,
            siteStopScheduleEntityParams.start_time
          ),
          'Asia/Seoul',
          "yyyy-MM-dd'T'HH:mm:ssXXX"
        ),
        endTime: formatInTimeZone(
          mergeDates(
            siteStopScheduleEntityParams.end_date,
            siteStopScheduleEntityParams.end_time
          ),
          'Asia/Seoul',
          "yyyy-MM-dd'T'HH:mm:ssXXX"
        ),
      };

      get<{ data: boolean }>('/customer/site/stop-schedule/exist', {
        params: requestBody,
      }).then((data) => {
        if (data.status === 'success') {
          if (data.data)
            setDateErrorMessage({
              isError: true,
              message: '이미 존재하는 정지일정 입니다.',
            });
          else
            setDateErrorMessage({
              isError: false,
              message: '',
            });
        }
      });
    }
  }, [siteStopScheduleEntityParams]);

  return (
    <div
      className={`flex flex-col items-end ${className}`}
      data-testid={testId}
    >
      <SVGImage
        className="mb-[15px] cursor-pointer"
        testId={`${testId}-close-button`}
        name="cancle"
        onClick={onClick}
      />
      <div className="w-full bg-white p-[40px] pt-[35px] [@media(max-height:1000px)]:pb-[30px]">
        <p
          className={`noto-semibold-4 text-dark-3`}
          data-testid={`${testId}-title`}
        >
          {t('overview.modal.title')}
        </p>
        <Divider className="mb-[14px] mt-[17px] bg-[#D6DEE4] [@media(max-height:1000px)]:mb-[8px]"></Divider>
        {/* 시작/종료 일정 설정 */}
        <p
          className={`noto-13-semibold mb-[10px] leading-[23px] text-dark-3 [@media(max-height:1000px)]:mb-[5px]`}
          data-testid={`${testId}-datepicker-title`}
        >
          {t('overview.modal.title', { context: 'datepicker' })}
        </p>
        <div className="flex justify-between">
          <div>
            <p
              className={`noto-11-bold leading-[22px] text-dark-6`}
              data-testid={`${testId}-datepicker-start-title`}
            >
              {t('overview.modal.title', { context: 'startDatepicker' })}
            </p>
            <div className="flex">
              <DateSelector
                disabled={isUpdate}
                date={siteStopScheduleEntityParams.start_date}
                inputBase={getInputBaseClassName(
                  PowerOutageScheduleDateType.START_DATE
                )}
                colors="text-dark-10 bg-white"
                onChange={(date: Date | null) => {
                  setChangedDateType(PowerOutageScheduleDateType.START_DATE);
                  setSiteStopScheduleEntityParams((prev) => ({
                    ...siteStopScheduleEntityParams,
                    [PowerOutageScheduleDateType.START_DATE]:
                      date ?? prev[PowerOutageScheduleDateType.START_DATE],
                  }));
                }}
                testId={`${testId}-start-date`}
              ></DateSelector>
              <PowerOutageScheduleTimepicker
                disabled={isUpdate}
                time={siteStopScheduleEntityParams.start_time}
                className={getInputBaseClassName(
                  PowerOutageScheduleDateType.START_TIME
                )}
                testId={`${testId}-start-time`}
                onChange={(date: Date | null) => {
                  setChangedDateType(PowerOutageScheduleDateType.START_TIME);
                  setSiteStopScheduleEntityParams((prev) => ({
                    ...siteStopScheduleEntityParams,
                    [PowerOutageScheduleDateType.START_TIME]:
                      date ?? prev[PowerOutageScheduleDateType.START_TIME],
                  }));
                }}
              ></PowerOutageScheduleTimepicker>
            </div>
          </div>
          <div>
            <p
              className={`noto-11-bold leading-[22px] text-dark-6`}
              data-testid={`${testId}-datepicker-end-title`}
            >
              {t('overview.modal.title', { context: 'endDatepicker' })}
            </p>
            <div className="flex">
              <DateSelector
                disabled={isUpdate}
                testId={`${testId}-end-date`}
                inputBase={getInputBaseClassName(
                  PowerOutageScheduleDateType.END_DATE
                )}
                colors="text-dark-10 bg-white"
                date={siteStopScheduleEntityParams.end_date}
                onChange={(date: Date | null) => {
                  setChangedDateType(PowerOutageScheduleDateType.END_DATE);
                  setSiteStopScheduleEntityParams((prev) => ({
                    ...siteStopScheduleEntityParams,
                    [PowerOutageScheduleDateType.END_DATE]:
                      date ?? prev[PowerOutageScheduleDateType.END_DATE],
                  }));
                }}
              ></DateSelector>
              <PowerOutageScheduleTimepicker
                disabled={isUpdate}
                time={siteStopScheduleEntityParams.end_time}
                className={getInputBaseClassName(
                  PowerOutageScheduleDateType.END_TIME
                )}
                testId={`${testId}-end-time`}
                onChange={(date: Date | null) => {
                  setChangedDateType(PowerOutageScheduleDateType.END_TIME);
                  setSiteStopScheduleEntityParams((prev) => ({
                    ...siteStopScheduleEntityParams,
                    [PowerOutageScheduleDateType.END_TIME]:
                      date ?? prev[PowerOutageScheduleDateType.END_TIME],
                  }));
                }}
              ></PowerOutageScheduleTimepicker>
            </div>
          </div>
        </div>
        {dateErrorMessage.isError && (
          <PowerOutageScheduleErrorMessage
            className="mt-[10px]"
            testId={`${testId}-date-error`}
            text={dateErrorMessage.message}
          />
        )}
        <Divider className="mb-[15px] mt-[20px] bg-[#D6DEE4] [@media(max-height:1000px)]:mb-[8px] [@media(max-height:1000px)]:mt-[15px]"></Divider>
        {/*  출력 상한 설정  */}
        <p
          className={`noto-13-semibold leading-[23px] text-dark-3`}
          data-testid={`${testId}-power-limit-title`}
        >
          {t('overview.modal.title', { context: 'powerLimit' })}
        </p>
        <div className="mt-[10px] flex [@media(max-height:1000px)]:mt-[5px]">
          <div>
            <p
              className={`noto-11-bold leading-[22px] text-dark-6`}
              data-testid={`${testId}-power-percent-title`}
            >
              {t('overview.modal.title', { context: 'percent' })}
            </p>
            <div className="flex items-center justify-center">
              <PowerOutageScheduleInput
                onChange={(value) =>
                  setAvailabilityData((prev) => ({
                    ...prev,
                    kw: value,
                    changed: 'kw',
                  }))
                }
                value={
                  availabilityData.kw !== null
                    ? availabilityData.kw.toLocaleString()
                    : ''
                }
                maxRange={kwMaxRange}
                className="w-[110px]"
                testId={`${testId}-power-kw-input`}
                suffix="kW"
              ></PowerOutageScheduleInput>
              <PowerOutageScheduleInput
                isFloat={true}
                onChange={(value) =>
                  setAvailabilityData((prev) => ({
                    ...prev,
                    powerPercent: value,
                    changed: 'powerPercent',
                  }))
                }
                value={
                  availabilityData.powerPercent !== null
                    ? availabilityData.powerPercent.toString()
                    : ''
                }
                className="ml-[10px] w-[90px]"
                testId={`${testId}-power-percent-input`}
                suffix="%"
              ></PowerOutageScheduleInput>
              <PowerOutageScheduleLimitApplyButton
                isDisabled={
                  filters.isIncludeLock || filters.searchKeyword.length > 0
                }
                className="ml-[10px]"
                testId={`${testId}-power-apply-button`}
                onClick={applyAvailabilityData}
              ></PowerOutageScheduleLimitApplyButton>
            </div>
          </div>
          <div className="relative ml-[25px]">
            <p
              className={`noto-11-bold leading-[22px] text-dark-6`}
              data-testid={`${testId}-power-search-title`}
            >
              {t('overview.modal.title', { context: 'search' })}
            </p>
            <div className="flex">
              <div className="relative">
                <PowerOutageScheduleLimitFilterButton
                  showFilter={showFilter}
                  onClick={setShowFilter}
                  className="relative"
                  testId={`${testId}-power-filter-button`}
                ></PowerOutageScheduleLimitFilterButton>
                {showFilterDot && (
                  <SVGImage
                    testId={`${testId}-power-filter-dot`}
                    name="filter-dot"
                    className="absolute -right-1 -top-1 z-10"
                  />
                )}
              </div>
              {hasKeywordFilter(filters) && (
                <PowerOutageScheduleLimitFilterBadge
                  onClick={() => setFilters({ ...filters, searchKeyword: '' })}
                  value={`${filters.type === PowerOutageScheduleFilterRadioType.Generator ? t('overview.modal.filter.generator') : t('overview.modal.filter.availability')}/${filters.searchKeyword}`}
                  className="ml-[10px]"
                  testId={`${testId}-power-keyword-filter-badge`}
                />
              )}
              {hasLockFilter(filters) && (
                <PowerOutageScheduleLimitFilterBadge
                  onClick={() =>
                    setFilters({ ...filters, isIncludeLock: false })
                  }
                  value={t('overview.modal.filter.lock', {
                    context: 'lock',
                  })}
                  className="ml-[10px]"
                  testId={`${testId}-power-lock-filter-badge`}
                />
              )}
            </div>
            {/* search filter */}
            {showFilter && (
              <PowerOutageScheduleFilter
                closeFilter={handleCloseFilter}
                onSubmit={filterData}
                className="absolute z-10 mt-[11px] h-fit min-h-[159px] w-fit min-w-[145px] rounded-[5px] border border-light-2 shadow-7"
                testId="add-power-outage-schedule-modal-filter"
              ></PowerOutageScheduleFilter>
            )}
          </div>
        </div>
        <PowerOutageScheduleLimit
          data={
            isMacbookAir
              ? filteredLimitData.slice(
                  currentIndex ? currentIndex * 9 : 0,
                  (currentIndex ? currentIndex * 9 : 0) + 9
                )
              : filteredLimitData.slice(
                  currentIndex ? currentIndex * 15 : 0,
                  (currentIndex ? currentIndex * 15 : 0) + 15
                )
          }
          onChange={(index, key: keyof PowerOutageScheduleLimitData, value) => {
            const copy = [...limitDataArray];
            switch (key) {
              case 'value':
                if (typeof value !== 'boolean') copy[index][key] = value;
                break;
              case 'isLock':
                if (typeof value === 'boolean') copy[index][key] = value;
                break;
            }

            setLimitDataArray(copy);
          }}
          testId="add-power-outage-schedule-modal-limit"
          errorIndex={limitDataError.isError ? limitDataError.index : null}
        />
        <div className="mt-[10px] flex items-center justify-between">
          <PowerOutageScheduleLimitPagination
            testId="add-power-outage-schedule-modal-power-limit-pagination"
            currentIndex={currentIndex}
            setIndex={setCurrentIndex}
            length={Math.ceil(
              isMacbookAir
                ? filteredLimitData.length / 9
                : filteredLimitData.length / 15
            )}
          />
          {availabilityDataError.isError && <></>}
          {limitDataError.isError && (
            <PowerOutageScheduleErrorMessage
              className="[&]:min-w-[253px] [&]:max-w-[400px]"
              testId={`${testId}-power-limit-error`}
              text={limitDataError.message}
            />
          )}
        </div>
        <Divider className="mb-[15px] mt-[21px] bg-[#D6DEE4] [@media(max-height:1000px)]:hidden"></Divider>
        <PowerOutageScheduleMemo
          onChange={handleTextChange}
          className="[@media(max-height:1000px)]:[&>p]:noto-11-bold [@media(max-height:1000px)]:mt-4 [@media(max-height:1000px)]:[&>textarea]:h-[32px]"
        />
        <Divider className="mb-[20px] mt-[18px] bg-[#3F4C5A]"></Divider>
        <div className="flex items-center justify-between">
          <PowerOutageScheduleResetButton
            onClick={() =>
              handleClearButton(
                limitDataInitialValue.map((data) => ({ ...data }))
              )
            }
            testId="add-power-outage-schedule-modal-power-reset-button"
          />
          <form onSubmit={handleSubmit}>
            <LoginSubmitButton
              text={t('overview.modal.limit.button', { context: 'submit' })}
              testId="add-power-outage-schedule-modal-power-submit-button"
              disabled={dateErrorMessage.isError}
              submitted={isSubmit}
            ></LoginSubmitButton>
          </form>
        </div>
      </div>
    </div>
  );
};
